import React, {ReactElement, useState} from 'react';
import {CaretIcon} from '@/web-components';
import moment from 'moment';
import styled from 'styled-components';

import {BUDGET_TYPE, MODULE_TYPES, PROJECT_STATUS, WorkflowCategories} from '../../../../constants';
import {InputLineInput, InputLineLabel, InputLineWrapper, ProjectStatusWrapper, SubSection} from './ProjectSettings_styled';
import {changeStage, changeTasksColumn, changeWinChance} from './ProjectSettingsMutations';
import {PercentageInputWrapper} from '../../../../components/inputs/percentage_input_wrapper';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {TASKS_OPTIONS} from '../../../../containers/modal/CloseProjectModal';
import {StatusTextClickableTile} from '../../../../containers/project/project_health/project_health_styles/ProjectHealthSidebar.styled';
import ProjectStatus from '../../../../containers/project/project_health/project_health_status/ProjectStatus';
import {CSS_CONSTANTS} from '../../../../css_variables';
import ProjectPriorityDropdown from './ProjectPriorityDropdown';
import {FormattedHTMLMessage, IntlShape} from 'react-intl';
import {hasModule} from '../../../shared/util/ModuleUtil';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {FEATURE_FLAGS} from '../../../../constants';
import {
	ButtonDropdownField,
	FlexRow,
	DropdownList,
	IconTooltip,
	Text,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import {DesignSystemDropdownOldLookAdapter} from '../../../shared/components/dropdowns/DesignSystemDropdownOldLookAdapter';
import {isProjectOpportunity} from '../../../shared/util/projectStageUtil';
import {StagePickerOption, selectedStageOption} from '../../../shared/util/projectStagePicker';
import {AsRead} from '../../../shared/util/AsRead';
import {ProjectSettingsPage_viewer$data} from './__generated__/ProjectSettingsPage_viewer.graphql';

type Project = AsRead<ProjectSettingsPage_viewer$data>['project'];
type TaskEdge = Project['tasks']['edges'][number];
type StatusNode = Project['projectStatuses']['edges'][number]['node'];
// order and jiraStatusId are not in the fragment, so under Relay's data masking they are never set.
type ColumnNode = Project['statusColumnsV2']['edges'][number]['node'] & {
	readonly order?: number;
	readonly jiraStatusId?: string;
};

type Props = {
	intl: IntlShape;
	projectId: string;
	project: Project;
	company: AsRead<ProjectSettingsPage_viewer$data>['company'];
	projectStage: Project['stage'] | null;
	stages: StagePickerOption[];
	hasOpportunityAccess: boolean;
	baselineWinChance: number | null;
	projectTasks: Project['tasks']['edges'];
	projectColumns: Project['statusColumnsV2']['edges'];
	currentProjectStatus: Project['currentProjectStatus'] | null;
	projectStatuses: Project['projectStatuses'];
	currentProjectStatusDate?: unknown;
	projectDoneOrHalted: boolean;
	budgetType: Project['budgetType'];
};

const statusCreatedAt = (node: StatusNode) =>
	moment.utc({
		y: node.year ?? undefined,
		M: Number(node.month) - 1,
		d: node.day ?? undefined,
		h: node.hour ?? undefined,
		m: node.minute ?? undefined,
		s: node.second ?? undefined,
	});

const StatusHistoryTile = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 34px;
`;

const TitleCaretWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	cursor: pointer;
`;

const StatusHistoryTitle = styled.div`
	color: ${CSS_CONSTANTS.v2_text_gray};
	font-weight: 500;
	font-size: 14px;
	margin-bottom: ${tokensForThemeProvider.sky.sys.spacing['200']};
`;

const showUpdateStatusModal = (projectId: string, projectDoneOrHalted: boolean): void => {
	if (!projectDoneOrHalted) {
		showModal({
			type: MODAL_TYPE.PROJECT_STATUS_V2,
			projectId: projectId,
		});
	}
};

const ProjectSettingsStatus = ({
	intl,
	projectId,
	project,
	company,
	projectStage,
	stages,
	hasOpportunityAccess,
	baselineWinChance,
	projectTasks,
	projectColumns,
	currentProjectStatus,
	projectStatuses,
	projectDoneOrHalted,
	budgetType,
}: Props): ReactElement => {
	const storedStatusHistoryExpanded = localStorage.getItem('settings_status_section_expanded');
	const [statusHistoryExpanded, setStatusHistoryExpanded] = useState<boolean>(
		storedStatusHistoryExpanded !== null ? JSON.parse(storedStatusHistoryExpanded) : true
	);

	const {formatMessage} = intl;

	const handleUnfinishedWork = (
		option: number,
		statusColumnId: string,
		notDoneTasks: ReadonlyArray<TaskEdge>,
		stageId: string
	): void => {
		switch (option) {
			case TASKS_OPTIONS.DONE: {
				const notDoneIds = notDoneTasks.map(task => task.node.id);
				changeTasksColumn(notDoneIds, statusColumnId, 'DONE');
				changeStage(projectId, stageId);
				break;
			}
			case TASKS_OPTIONS.STAY:
				changeStage(projectId, stageId);
				break;
			default:
				break;
		}
	};

	const handleChangeStage = (stage: StagePickerOption): void => {
		if (stage) {
			if (stage.profile === PROJECT_STATUS.DONE) {
				const notDoneTasks = projectTasks.filter(task => task.node.statusColumnV2.category !== PROJECT_STATUS.DONE);
				if (notDoneTasks.length > 0) {
					const doneColumns = projectColumns
						.filter(col => col.node.category === WorkflowCategories.DONE)
						.map(col => col.node as ColumnNode)
						.sort((a, b) => Number(a.order) - Number(b.order))
						.map(node => ({
							value: node.id,
							label: node.name,
							logo: node.jiraStatusId ? 'jira-logo' : undefined,
						}));
					showModal({
						type: MODAL_TYPE.CLOSE_PROJECT,
						tasksList: notDoneTasks,
						doneColumns: doneColumns,
						callback: (option: number, statucColumnId: string) =>
							handleUnfinishedWork(option, statucColumnId, notDoneTasks, stage.value),
					});
				} else {
					changeStage(projectId, stage.value);
				}
			} else {
				changeStage(projectId, stage.value);
			}
		}
	};
	const toggleStatusHistoryExpanded = () => {
		const newStatusState = !statusHistoryExpanded;
		localStorage.setItem('settings_status_section_expanded', String(newStatusState));
		setStatusHistoryExpanded(newStatusState);
	};

	const oldProjectStatuses = projectStatuses.edges.filter(
		projectStatus => projectStatus.node.id !== currentProjectStatus?.id
	);

	return (
		<>
			<SubSection title={formatMessage({id: 'settings.project-stage'})}>
				<InputLineWrapper>
					<InputLineLabel>{formatMessage({id: 'settings.project-stage'})}</InputLineLabel>
					<InputLineInput>
						<DesignSystemDropdownOldLookAdapter>
							<DropdownList>
								<DropdownList.Trigger data-cy={'settings-project-stage'}>
									<ButtonDropdownField>
										{projectStage ? (
											<Text>{selectedStageOption(stages, projectStage)?.label}</Text>
										) : (
											<Text color={Text.Color.auxiliary}>
												{formatMessage({id: 'settings.project-stage'})}
											</Text>
										)}
									</ButtonDropdownField>
								</DropdownList.Trigger>
								<DropdownList.Content align={'start'}>
									{stages.map(stage => (
										<DropdownList.Item key={stage.value} onClick={() => handleChangeStage(stage)}>
											<FlexRow>
												<Text>{stage.label}</Text>
											</FlexRow>
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList>
						</DesignSystemDropdownOldLookAdapter>
					</InputLineInput>
				</InputLineWrapper>
				{hasOpportunityAccess && budgetType !== BUDGET_TYPE.NON_BILLABLE && (
					<>
						<InputLineWrapper>
							<InputLineLabel>
								{formatMessage({id: 'common.win_probability'})}
								{/* The tooltip sends the reader to the admin stages page, which needs the flag as well
								    as the module. The surrounding block gates on the module alone and predates this
								    page, so the flag is checked here rather than there: widening that condition would
								    hide the win probability field itself from accounts that have always had it. */}
								{hasFeatureFlag(FEATURE_FLAGS.OPPORTUNITIES) && hasModule(MODULE_TYPES.OPPORTUNITIES) && (
									<IconTooltip>
										<IconTooltip.Item
											title={formatMessage({
												id: 'settings.opportunity_stages.legacy_tooltip.title',
											})}
											details={formatMessage({
												id: 'settings.opportunity_stages.legacy_tooltip.details',
											})}
										/>
									</IconTooltip>
								)}
							</InputLineLabel>
							<InputLineInput>
								<PercentageInputWrapper
									width={'70px'}
									disabled={!isProjectOpportunity(project)}
									value={Number(baselineWinChance) * 100}
									callback={(win: number) => changeWinChance(projectId, win)}
									zeroToHundred={true}
								/>
							</InputLineInput>
						</InputLineWrapper>
						{isProjectOpportunity(project) ? (
							<InputLineWrapper>
								<FormattedHTMLMessage
									id={
										hasModule(MODULE_TYPES.CALC_WIN_PERCENTAGE)
											? 'project_settings.win_chance_description_calc_win'
											: 'project_settings.win_chance_description'
									}
								/>
							</InputLineWrapper>
						) : null}
					</>
				)}
			</SubSection>
			{
				<SubSection title={formatMessage({id: 'project_settings.project_priority'})}>
					<InputLineWrapper>
						<InputLineLabel>{formatMessage({id: 'project_settings.priority_level'})}</InputLineLabel>
						<InputLineInput>
							<ProjectPriorityDropdown project={project} company={company} />
						</InputLineInput>
					</InputLineWrapper>
				</SubSection>
			}
			<SubSection title={formatMessage({id: 'settings.project-status'})}>
				<ProjectStatusWrapper>
					<>
						<StatusTextClickableTile
							onClick={() => showUpdateStatusModal(projectId, projectDoneOrHalted)}
							disabled={projectDoneOrHalted}
						>
							{projectDoneOrHalted
								? formatMessage({id: 'settings.project_status.locked'})
								: formatMessage({id: 'settings.project_status.prompt'})}
						</StatusTextClickableTile>
						{currentProjectStatus && <ProjectStatus projectStatus={currentProjectStatus} />}
						{oldProjectStatuses && oldProjectStatuses.length > 0 ? (
							<StatusHistoryTile>
								<TitleCaretWrapper onClick={() => toggleStatusHistoryExpanded()}>
									<StatusHistoryTitle>
										{formatMessage({id: 'settings.project_status.history'})}
									</StatusHistoryTitle>
									<CaretIcon
										direction={statusHistoryExpanded ? CaretIcon.DIRECTION.UP : CaretIcon.DIRECTION.DOWN}
									/>
								</TitleCaretWrapper>
								{statusHistoryExpanded &&
									oldProjectStatuses
										.sort((a, b) => (statusCreatedAt(a.node).isBefore(statusCreatedAt(b.node)) ? 1 : -1))
										.map(projectStatus => (
											<ProjectStatus key={projectStatus.node.id} projectStatus={projectStatus.node} />
										))}
							</StatusHistoryTile>
						) : null}
					</>
				</ProjectStatusWrapper>
			</SubSection>
		</>
	);
};

export default ProjectSettingsStatus;
